import { EApplicantType, ECreditApplicationStatus, EDecision } from '@if/core/types'
import { Button, Card, CardActions, CardContent, Typography } from '@mui/material'
import { useResetVouched } from '@src/data/api/credit-application/credit-application-api'
import { CreditApplication, User } from '@src/data/types'
import { reportErrorToConsole } from '@src/services/error-logger'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplication: CreditApplication
  applicantType: EApplicantType
  user: User | null
  editDisabled: boolean
  // B2C runs Vouched inside the Draft soft-hit flow → only enable the reset while still Draft.
  // Personal runs Vouched after approval (thin-file payout gate) → enable post-Draft + Approved,
  // following the same shape as the Flinks reset (see FlinksActionsSection).
  requireDraftOnly?: boolean
}

const VouchedSection = ({ creditApplication, applicantType, user, editDisabled, requireDraftOnly = false }: Props) => {
  const { t } = useTranslation()
  const [resetVouchedHook] = useResetVouched()
  const [loading, setLoading] = useState(false)

  const resetVouched = () => {
    setLoading(true)
    resetVouchedHook({
      creditApplicationId: creditApplication.id,
      financingProgramId: creditApplication.financingProgramId,
      applicantType,
    })
      .then(() => {
        setLoading(false)
      })
      .catch(reportErrorToConsole)
  }

  const isDraft = creditApplication.status === ECreditApplicationStatus.Draft
  const isApproved = creditApplication.finalCreditDecision?.decision === EDecision.Approved

  const disabled = requireDraftOnly
    ? !isDraft || loading
    : !user?.rights.canEditCreditApp || isDraft || !isApproved || editDisabled || loading

  const titleKey = applicantType === EApplicantType.CoApplicant ? 'credit.vouched.titleCoApplicant' : 'credit.vouched.title'

  return (
    <Card elevation={3} sx={{ mt: 3, mb: 3 }}>
      <CardContent>
        <Typography component="div" variant="h5">
          {t(titleKey)}
        </Typography>
      </CardContent>
      <CardActions>
        <Button disabled={disabled} onClick={resetVouched}>
          {t('credit.vouched.reset')}
        </Button>
      </CardActions>
    </Card>
  )
}

export default memo(VouchedSection)
